import { useQuery } from '@tanstack/react-query';
import { appsApi } from '../features/apps/api.ts';
import { subscriptionsApi } from '../features/subscriptions/api.ts';
import { AppCard } from '../features/dashboard/AppCard.tsx';
import { useMe } from '../features/dashboard/hooks.ts';

function AppCardSkeleton(): JSX.Element {
  return (
    <div className="card-paper p-5 flex min-h-[188px] flex-col gap-4 animate-pulse">
      <div className="size-11 rounded-xl bg-muted" />
      <div className="flex-1 space-y-2">
        <div className="h-5 w-2/3 rounded bg-muted" />
        <div className="h-3.5 w-full rounded bg-muted" />
      </div>
      <div className="h-4 w-16 rounded bg-muted" />
    </div>
  );
}

// Office's home is the launcher, and only the launcher.
//
// It used to carry an IdentityCard restating the top bar (name, org, avatar)
// and duplicating /profile, plus a PlanCard that read "Not set" for most users,
// under a three-line header that said one thing. All of that is gone: identity
// lives in the top bar and /profile, plan lives in /profile, and the plan only
// interrupts here when it actually gates launching (GRACE/BLOCKED).
export function Dashboard(): JSX.Element {
  const { me } = useMe();
  const { data: apps, isLoading: appsLoading } = useQuery({
    queryKey: ['apps', 'status'],
    queryFn: () => appsApi.status(),
  });
  const { data: sub } = useQuery({
    queryKey: ['subscription', 'status'],
    queryFn: subscriptionsApi.myStatus,
  });

  const critical = sub?.status === 'GRACE' || sub?.status === 'BLOCKED';
  const firstName = me?.name?.split(' ')[0];

  const grid = 'grid gap-4 sm:grid-cols-2 xl:grid-cols-3';

  return (
    <div className="page">
      <div className="w-full space-y-8">
        {/* The only plan state worth interrupting for: it gates launching. */}
        {critical && sub?.banner && (
          <div
            role="alert"
            className={`rounded-xl px-4 py-3 text-sm ${
              sub.status === 'BLOCKED'
                ? 'bg-[hsl(var(--error-container))] text-[hsl(var(--on-error-container))]'
                : 'bg-[hsl(var(--warning-container))] text-[hsl(var(--on-warning-container))]'
            }`}
          >
            {sub.banner}
          </div>
        )}

        {/* One M3 display headline. The greeting carries the personalisation
            that used to need its own line. */}
        <header>
          <h1 className="text-[2rem] md:text-[2.5rem] font-normal leading-tight text-ink">
            {firstName ? `Welcome back, ${firstName}` : 'Your apps'}
          </h1>
        </header>

        {appsLoading ? (
          <div className={grid} role="status" aria-live="polite">
            <span className="sr-only">Loading your apps…</span>
            <AppCardSkeleton />
            <AppCardSkeleton />
            <AppCardSkeleton />
            <AppCardSkeleton />
          </div>
        ) : (
          <div className={grid}>
            {apps?.map((app) => <AppCard key={`${app.key}:${app.tile ?? ''}`} app={app} />)}
          </div>
        )}
      </div>
    </div>
  );
}
